import { Component, createRef } from "react"

/* Components */
import { Pagination } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* Moment */
import moment from "moment"

/* REST API */
import { bookings } from "../../api/Organization"

/* Helpers */
import { utils } from "../../helpers"

/* Widgets */
import BookingsFilterModal, { INITIAL_BOOKING_FILTER, PERIODS } from "./BookingsFilterModal"
import { DetailsModal } from "../booking"

/* Shared */
import { BOOKING_STATUSES, CHANNELS, metric, number } from "./shared"


/* Widget Bookings Tab — the visits of the point over a period. It opens on the current month
   and the period is moved by hand, so the counters always belong to what is on the screen */
class BookingsTab extends Component {

    constructor() {
        super()

        this.state = {

            status: "loading",

            list: [],
            count: 0,
            page: 1,
            statistics: null,
            channels: [],

            filter: { ...INITIAL_BOOKING_FILTER }
        }

        /* The filters and the card of one visit are opened by the reference */
        this._filter = createRef()
        this._booking = createRef()
    }

    componentDidMount = () => {
        this.load(1)
    }

    /* Load the records of the period */
    load = (page = 1) => {

        /* Fields */
        const { filter } = this.state
        const { data } = this.props

        this.setState({ status: "loading", page })

        bookings({
            id: data.organization.id,
            startDate: moment(filter.startDate).format("YYYY-MM-DD"),
            endDate: moment(filter.endDate).format("YYYY-MM-DD"),
            status: filter.status,
            channel: filter.channel,
            page
        }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", list: response.data.list, count: response.data.count, statistics: response.data.statistics, channels: response.data.channels })
            }
            else {
                this.setState({ status: "error", list: [], count: 0 })
            }
        }).catch(() => this.setState({ status: "error", list: [], count: 0 }))
    }

    /* Open the card of one visit — the name of the point travels with the row, so the head of
       the card is whole while it is still loading */
    open = booking => {

        /* Fields */
        const { data } = this.props

        this._booking.current.open({ ...booking, organization: data.organization.name, organizationImage: data.organization.image })
    }


    /* What the period is called on the button */
    title = () => {

        /* Fields */
        const { filter } = this.state

        const period = PERIODS.find(item => item.key === filter.period)

        if (period && period.key !== "period") {
            return period.text
        }

        return `${moment(filter.startDate).format("DD.MM.YY")} — ${moment(filter.endDate).format("DD.MM.YY")}`
    }

    /* How many values of the filter differ from the empty one */
    filters = () => {

        /* Fields */
        const { filter } = this.state

        let count = filter.period === INITIAL_BOOKING_FILTER.period ? 0 : 1

        if (`${filter.status}` !== "all") {
            count = count + 1
        }

        if (filter.channel !== "all") {
            count = count + 1
        }

        return count
    }

    /* The list of the records of the period */
    _list = () => {

        /* Fields */
        const { status, list, count, page } = this.state

        if (status === "loading") {
            return (
                <div className="organization-details-loading small"><LoadingOutlined /></div>
            )
        }

        if (status !== "ok") {
            return (
                <div className="organization-details-empty">Не удалось загрузить записи</div>
            )
        }

        if (list.length === 0) {
            return (
                <div className="organization-details-empty">За выбранный период записей нет</div>
            )
        }

        return (
            <>
                {list.map(booking => {

                    const state = BOOKING_STATUSES.find(item => item.status === parseInt(booking.status))
                    const price = booking.finalPrice ? booking.finalPrice : (booking.endTotal ? booking.endTotal : booking.total)

                    return (
                        <div onClick={() => this.open(booking)} key={`${booking.id}`} className="organization-details-row clickable">

                            <div className="organization-details-row-main">
                                <div className="organization-details-row-title">{utils.check(booking.user, "Без имени")}</div>
                                <div className="organization-details-row-text">
                                    №{booking.number} · {utils.check(booking.member, "-")} · {moment(booking.reservedTime).format("DD.MM.YYYY HH:mm")}
                                    {booking.channel ? ` · ${utils.check(CHANNELS[booking.channel], booking.channel)}` : ""}
                                </div>
                            </div>

                            <div className="organization-details-row-side">
                                <div className="organization-details-row-price">{utils.convertor(price, "₸", 1)}</div>
                                {state && <div className={`tag ${state.color}`}>{state.text}</div>}
                            </div>

                        </div>
                    )
                })}

                <Pagination size={20} page={page} total={count} onPageChange={page => this.load(page)} />
            </>
        )
    }

    render = () => {

        /* Fields */
        const { statistics, channels, filter } = this.state

        const total = statistics ? number(statistics.total) : 0
        const active = this.filters()

        return (
            <div className="organization-details-body">

                <div className="organization-details-toolbar">

                    <div className="organization-details-period">
                        <span>Период</span>
                        {this.title()}
                    </div>

                    <div onClick={() => this._filter.current.open(filter)} className={`organization-details-action ${active > 0 ? "primary" : ""}`}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M1.5 3.5H14.5M4 8H12M6.5 12.5H9.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                        <span>Фильтр{active > 0 ? ` · ${active}` : ""}</span>
                    </div>

                </div>

                {statistics &&
                    <div className="organization-details-metrics">
                        {metric("Всего записей", total)}
                        {metric("Ожидание", number(statistics.waiting), "orange")}
                        {metric("Приняты", number(statistics.accepted), "purple")}
                        {metric("Завершены", number(statistics.completed), "green")}
                        {metric("Отменены", number(statistics.cancelled) + number(statistics.missed), "red")}
                        {metric("Выручка", utils.convertor(Math.round(number(statistics.revenue)), "₸", 1), "blue")}
                    </div>
                }

                <div className="organization-details-columns">

                    <div className="organization-details-column">

                        <div className="organization-details-section">

                            <div className="organization-details-section-text">Каналы записи</div>

                            {channels.length === 0 && <div className="organization-details-empty">За период записей нет</div>}

                            {channels.map(channel =>
                                <div key={channel.channel} className="organization-details-channel">
                                    <div className="organization-details-channel-text">{utils.check(CHANNELS[channel.channel], channel.channel)}</div>
                                    <div className="organization-details-channel-line">
                                        <div className="organization-details-channel-fill" style={{ width: `${total > 0 ? Math.round(number(channel.count) * 100 / total) : 0}%` }} />
                                    </div>
                                    <div className="organization-details-channel-count">{channel.count}</div>
                                </div>
                            )}

                        </div>

                    </div>

                    <div className="organization-details-column">

                        <div className="organization-details-section">
                            <div className="organization-details-section-text">Записи за период</div>
                            {this._list()}
                        </div>

                    </div>

                </div>

                {/* Draw Filters */}
                <BookingsFilterModal ref={this._filter} onApply={filter => this.setState({ filter }, () => this.load(1))} />

                {/* Draw Booking */}
                <DetailsModal ref={this._booking} onChanged={() => this.load(this.state.page)} />

            </div>
        )
    }
}

export default BookingsTab
